import React, { useState, useEffect, useCallback } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Home,
  Lightbulb,
  LightbulbOff,
  Server,
  Webhook,
  Plus,
  Trash2,
  Pencil,
  Play,
  Power,
  Sun,
  Moon,
  Loader2,
  Settings,
  X,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import {
  getSmartHomeSettings,
  saveSmartHomeSettings,
  WebhookButton,
  deleteWebhookButton,
  SmartHomeSettings,
} from "@/lib/smartHomeSettings";
import { Switch } from "@/components/ui/switch";

// URL validation to prevent SSRF attacks
const isValidSmartHomeUrl = (url: string, type: 'hue' | 'ha' | 'webhook'): { valid: boolean; error?: string } => {
  try {
    // For Philips Hue, validate IP address format
    if (type === 'hue') {
      // Must be a valid local IP (192.168.x.x, 10.x.x.x, 172.16-31.x.x)
      const localIPPattern = /^(192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3})$/;
      if (!localIPPattern.test(url)) {
        return { valid: false, error: "Philips Hue bridge must be on local network (192.168.x.x, 10.x.x.x, or 172.16-31.x.x)" };
      }
      // Block metadata endpoints
      if (url.includes('169.254.169.254') || url.includes('metadata')) {
        return { valid: false, error: "Invalid IP address" };
      }
      return { valid: true };
    }

    // For Home Assistant and webhooks, validate URL
    const parsedUrl = new URL(url);
    
    // Block dangerous protocols
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return { valid: false, error: "Only HTTP and HTTPS protocols are allowed" };
    }

    // Block localhost and loopback for webhooks (but allow for HA on local network)
    if (type === 'webhook') {
      if (parsedUrl.hostname === 'localhost' || parsedUrl.hostname === '127.0.0.1') {
        return { valid: false, error: "Localhost URLs are not allowed for webhooks" };
      }
    }

    // Block cloud metadata endpoints
    const blockedHosts = ['169.254.169.254', 'metadata.google.internal', 'metadata.azure.com'];
    if (blockedHosts.some(host => parsedUrl.hostname.includes(host))) {
      return { valid: false, error: "This URL is not allowed" };
    }

    // Block internal AWS/GCP/Azure metadata paths
    if (parsedUrl.pathname.includes('/latest/meta-data') || 
        parsedUrl.pathname.includes('/metadata/') ||
        parsedUrl.pathname.includes('/computeMetadata/')) {
      return { valid: false, error: "Metadata endpoints are not allowed" };
    }

    return { valid: true };
  } catch {
    return { valid: false, error: "Invalid URL format" };
  }
};

const buttonIcons = [
  { id: "power", icon: Power, label: "הפעלה" },
  { id: "lightbulb", icon: Lightbulb, label: "מנורה" },
  { id: "sun", icon: Sun, label: "שמש" },
  { id: "moon", icon: Moon, label: "ירח" },
  { id: "home", icon: Home, label: "בית" },
  { id: "play", icon: Play, label: "הפעל" },
];

const buttonColors = [
  { id: "teal", name: "טורקיז", bg: "bg-primary/20", border: "border-primary", text: "text-primary" },
  { id: "orange", name: "כתום", bg: "bg-orange-500/20", border: "border-orange-500", text: "text-orange-500" },
  { id: "blue", name: "כחול", bg: "bg-blue-500/20", border: "border-blue-500", text: "text-blue-500" },
  { id: "green", name: "ירוק", bg: "bg-green-500/20", border: "border-green-500", text: "text-green-500" },
  { id: "purple", name: "סגול", bg: "bg-purple-500/20", border: "border-purple-500", text: "text-purple-500" },
  { id: "pink", name: "ורוד", bg: "bg-pink-500/20", border: "border-pink-500", text: "text-pink-500" },
  { id: "yellow", name: "צהוב", bg: "bg-yellow-500/20", border: "border-yellow-500", text: "text-yellow-600" },
  { id: "red", name: "אדום", bg: "bg-red-500/20", border: "border-red-500", text: "text-red-500" },
];

interface HueLight {
  id: string;
  name: string;
  on: boolean;
  brightness: number;
}

interface HAEntity {
  entity_id: string;
  friendly_name: string;
  state: string;
}

export default function SmartHomePage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<SmartHomeSettings>(getSmartHomeSettings());
  const [activeTab, setActiveTab] = useState("buttons");
  
  // Philips Hue state
  const [hueLights, setHueLights] = useState<HueLight[]>([]);
  const [loadingHue, setLoadingHue] = useState(false);
  
  // Home Assistant state
  const [haEntities, setHaEntities] = useState<HAEntity[]>([]);
  const [loadingHA, setLoadingHA] = useState(false);
  
  // Button editing - now uses navigation instead of dialog
  const [editingButton, setEditingButton] = useState<WebhookButton | null>(null);
  
  // Webhook triggering state
  const [triggeringButton, setTriggeringButton] = useState<string | null>(null);


  // Load Philips Hue lights
  const loadHueLights = useCallback(async () => {
    if (!settings.philipsHue.enabled || !settings.philipsHue.bridgeIp || !settings.philipsHue.username) return;
    
    // Validate IP before making request
    const validation = isValidSmartHomeUrl(settings.philipsHue.bridgeIp, 'hue');
    if (!validation.valid) {
      toast.error(validation.error || "Invalid Hue bridge IP");
      return;
    }
    
    setLoadingHue(true);
    try {
      const response = await fetch(
        `http://${settings.philipsHue.bridgeIp}/api/${settings.philipsHue.username}/lights`
      );
      const data = await response.json();
      const lights: HueLight[] = Object.entries(data).map(([id, light]: [string, any]) => ({
        id,
        name: light.name,
        on: light.state.on,
        brightness: Math.round((light.state.bri / 254) * 100),
      }));
      setHueLights(lights);
    } catch (e) {
      toast.error("שגיאה בטעינת מנורות Hue");
    } finally {
      setLoadingHue(false);
    }
  }, [settings.philipsHue]);

  // Toggle Hue light
  const toggleHueLight = async (lightId: string, on: boolean) => {
    // Validate IP before making request
    const validation = isValidSmartHomeUrl(settings.philipsHue.bridgeIp, 'hue');
    if (!validation.valid) {
      toast.error(validation.error || "Invalid Hue bridge IP");
      return;
    }
    
    try {
      await fetch(
        `http://${settings.philipsHue.bridgeIp}/api/${settings.philipsHue.username}/lights/${lightId}/state`,
        {
          method: "PUT",
          body: JSON.stringify({ on }),
        }
      );
      setHueLights((prev) =>
        prev.map((l) => (l.id === lightId ? { ...l, on } : l))
      );
      toast.success(on ? "המנורה הודלקה" : "המנורה כבתה");
    } catch {
      toast.error("שגיאה בשליטה במנורה");
    }
  };

  // Set Hue light brightness
  const setHueBrightness = async (lightId: string, brightness: number) => {
    // Validate IP before making request
    const validation = isValidSmartHomeUrl(settings.philipsHue.bridgeIp, 'hue');
    if (!validation.valid) {
      toast.error(validation.error || "Invalid Hue bridge IP");
      return;
    }
    
    try {
      await fetch(
        `http://${settings.philipsHue.bridgeIp}/api/${settings.philipsHue.username}/lights/${lightId}/state`,
        {
          method: "PUT",
          body: JSON.stringify({ bri: Math.round((brightness / 100) * 254) }),
        }
      );
      setHueLights((prev) =>
        prev.map((l) => (l.id === lightId ? { ...l, brightness } : l))
      );
    } catch {
      toast.error("שגיאה בהגדרת בהירות");
    }
  };

  // Load Home Assistant entities
  const loadHAEntities = useCallback(async () => {
    if (!settings.homeAssistant.enabled || !settings.homeAssistant.serverUrl || !settings.homeAssistant.accessToken) return;
    
    // Validate URL before making request
    const validation = isValidSmartHomeUrl(settings.homeAssistant.serverUrl, 'ha');
    if (!validation.valid) {
      toast.error(validation.error || "Invalid Home Assistant URL");
      return;
    }
    
    setLoadingHA(true);
    try {
      const response = await fetch(`${settings.homeAssistant.serverUrl}/api/states`, {
        headers: {
          Authorization: `Bearer ${settings.homeAssistant.accessToken}`,
        },
      });
      const data = await response.json();
      const entities: HAEntity[] = data
        .filter((e: any) => e.entity_id.startsWith("light.") || e.entity_id.startsWith("switch."))
        .map((e: any) => ({
          entity_id: e.entity_id,
          friendly_name: e.attributes.friendly_name || e.entity_id,
          state: e.state,
        }));
      setHaEntities(entities);
    } catch {
      toast.error("שגיאה בטעינת Home Assistant");
    } finally {
      setLoadingHA(false);
    }
  }, [settings.homeAssistant]);

  // Toggle Home Assistant entity
  const toggleHAEntity = async (entityId: string) => {
    // Validate URL before making request
    const validation = isValidSmartHomeUrl(settings.homeAssistant.serverUrl, 'ha');
    if (!validation.valid) {
      toast.error(validation.error || "Invalid Home Assistant URL");
      return;
    }
    
    try {
      await fetch(`${settings.homeAssistant.serverUrl}/api/services/homeassistant/toggle`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${settings.homeAssistant.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ entity_id: entityId }),
      });
      await loadHAEntities();
      toast.success("הפעולה בוצעה");
    } catch {
      toast.error("שגיאה בשליטה בהתקן");
    }
  };

  // Trigger webhook
  const triggerWebhook = async (button: WebhookButton) => {
    // Validate URL before making request
    const validation = isValidSmartHomeUrl(button.webhookUrl, 'webhook');
    if (!validation.valid) {
      toast.error(validation.error || "Invalid webhook URL");
      return;
    }
    
    setTriggeringButton(button.id);
    try {
      await fetch(button.webhookUrl, {
        method: button.method,
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: button.method === "POST" ? JSON.stringify({
          triggered_from: "ayuni",
          button_name: button.name,
          timestamp: new Date().toISOString(),
        }) : undefined,
      });
      toast.success(`${button.name} הופעל!`);
    } catch {
      toast.error("שגיאה בהפעלת הכפתור");
    } finally {
      setTriggeringButton(null);
    }
  };

  // Delete button
  const handleDeleteButton = (id: string) => {
    deleteWebhookButton(id);
    setSettings(getSmartHomeSettings());
    toast.success("הכפתור נמחק");
  };

  // Edit button - navigate to edit page
  const handleEditButton = (button: WebhookButton) => {
    navigate(`/smarthome/button?id=${button.id}`);
  };

  useEffect(() => {
    if (settings.philipsHue.enabled) loadHueLights();
    if (settings.homeAssistant.enabled) loadHAEntities();
  }, [settings.philipsHue.enabled, settings.homeAssistant.enabled, loadHueLights, loadHAEntities]);

  const getButtonColor = (colorId: string) => buttonColors.find((c) => c.id === colorId) || buttonColors[0];
  const getButtonIcon = (iconId: string) => buttonIcons.find((i) => i.id === iconId)?.icon || Power;

  return (
    <MainLayout>
      <div className="flex-1 flex flex-col overflow-hidden" dir="rtl">
        {/* Header */}
        <header className="flex items-center gap-3 px-6 py-4 border-b border-border bg-card/50">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="rounded-xl">
            <ArrowRight className="w-6 h-6" />
          </Button>
          <Home className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold">בית חכם</h1>
        </header>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="px-6 pt-4">
            <TabsList className="grid w-full max-w-lg grid-cols-4">
              <TabsTrigger value="buttons" className="gap-2">
                <Webhook className="w-4 h-4" />
                כפתורים
              </TabsTrigger>
              <TabsTrigger value="hue" className="gap-2">
                <Lightbulb className="w-4 h-4" />
                Hue
              </TabsTrigger>
              <TabsTrigger value="ha" className="gap-2">
                <Server className="w-4 h-4" />
                HA
              </TabsTrigger>
              <TabsTrigger value="settings" className="gap-2">
                <Settings className="w-4 h-4" />
                הגדרות
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Webhook Buttons Tab */}
          <TabsContent value="buttons" className="flex-1 overflow-auto p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {settings.webhookButtons.map((button, index) => {
                const color = getButtonColor(button.color);
                const Icon = getButtonIcon(button.icon);
                const isTriggering = triggeringButton === button.id;

                return (
                  <motion.div
                    key={button.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="relative group"
                  >
                    <button
                      onClick={() => triggerWebhook(button)}
                      disabled={isTriggering}
                      data-scan-item
                      className={cn(
                        "w-full aspect-square rounded-2xl border-2 flex flex-col items-center justify-center gap-3",
                        "transition-all duration-200 active:scale-95 hover:scale-105 hover:shadow-lg",
                        color.bg,
                        color.border
                      )}
                    >
                      {isTriggering ? (
                        <Loader2 className={cn("w-12 h-12 animate-spin", color.text)} />
                      ) : (
                        <Icon className={cn("w-12 h-12", color.text)} />
                      )}
                      <span className={cn("text-lg font-bold", color.text)}>{button.name}</span>
                    </button>
                    
                    {/* Edit/Delete buttons */}
                    <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-8 h-8 bg-card/80"
                        onClick={(e) => { e.stopPropagation(); handleEditButton(button); }}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-8 h-8 bg-card/80 text-destructive"
                        onClick={(e) => { e.stopPropagation(); handleDeleteButton(button.id); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                );
              })}

              {/* Add Button */}
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={() => navigate("/smarthome/button")}
                data-scan-item
                className={cn(
                  "aspect-square rounded-2xl border-2 border-dashed border-primary/40",
                  "flex flex-col items-center justify-center gap-3",
                  "bg-primary/5 hover:bg-primary/10 hover:border-primary",
                  "transition-all duration-200 active:scale-95"
                )}
              >
                <Plus className="w-12 h-12 text-primary" />
                <span className="text-lg font-bold text-primary">הוסף כפתור</span>
              </motion.button>
            </div>
          </TabsContent>

          {/* Philips Hue Tab */}
          <TabsContent value="hue" className="flex-1 overflow-auto p-6">
            {loadingHue ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : hueLights.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Lightbulb className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">לא נמצאו מנורות</p>
                <p className="text-sm">וודא שהחיבור ל-Hue תקין</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {hueLights.map((light) => (
                  <div
                    key={light.id}
                    className={cn(
                      "p-4 rounded-2xl border-2 transition-all",
                      light.on
                        ? "bg-yellow-500/10 border-yellow-500"
                        : "bg-card border-border"
                    )}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {light.on ? (
                          <Lightbulb className="w-8 h-8 text-yellow-500" />
                        ) : (
                          <LightbulbOff className="w-8 h-8 text-muted-foreground" />
                        )}
                        <span className="font-bold">{light.name}</span>
                      </div>
                      <Button
                        variant={light.on ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleHueLight(light.id, !light.on)}
                      >
                        {light.on ? "כבה" : "הדלק"}
                      </Button>
                    </div>
                    {light.on && (
                      <div className="space-y-2">
                        <Label>בהירות: {light.brightness}%</Label>
                        <Slider
                          value={[light.brightness]}
                          onValueChange={([v]) => setHueBrightness(light.id, v)}
                          min={1}
                          max={100}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Home Assistant Tab */}
          <TabsContent value="ha" className="flex-1 overflow-auto p-6">
            {loadingHA ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : haEntities.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Server className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">לא נמצאו התקנים</p>
                <p className="text-sm">וודא שהחיבור ל-Home Assistant תקין</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {haEntities.map((entity) => {
                  const isOn = entity.state === "on";
                  return (
                    <button
                      key={entity.entity_id}
                      onClick={() => toggleHAEntity(entity.entity_id)}
                      className={cn(
                        "p-4 rounded-2xl border-2 flex flex-col items-center justify-center gap-3",
                        "transition-all duration-200 active:scale-95 hover:scale-105",
                        isOn
                          ? "bg-green-500/10 border-green-500"
                          : "bg-card border-border"
                      )}
                    >
                      <Power className={cn("w-10 h-10", isOn ? "text-green-500" : "text-muted-foreground")} />
                      <span className="font-medium text-sm text-center">{entity.friendly_name}</span>
                      <span className={cn("text-xs", isOn ? "text-green-500" : "text-muted-foreground")}>
                        {isOn ? "פועל" : "כבוי"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="flex-1 overflow-auto p-6">
            <div className="max-w-2xl mx-auto space-y-6">
              {/* Philips Hue Settings */}
              <div className={cn(
                "rounded-xl border p-4 space-y-4",
                settings.philipsHue.enabled ? "border-yellow-500 bg-yellow-500/5" : "border-border"
              )}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Lightbulb className={cn("w-6 h-6", settings.philipsHue.enabled ? "text-yellow-500" : "text-muted-foreground")} />
                    <h3 className="font-bold">Philips Hue</h3>
                  </div>
                  <Switch
                    checked={settings.philipsHue.enabled}
                    onCheckedChange={(checked) => {
                      const newSettings = saveSmartHomeSettings({
                        philipsHue: { ...settings.philipsHue, enabled: checked }
                      });
                      setSettings(newSettings);
                    }}
                  />
                </div>
                
                {settings.philipsHue.enabled && (
                  <div className="space-y-3 pt-2 border-t border-border/50">
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm">
                      <p className="font-medium mb-2">הוראות התחברות:</p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>מצא את כתובת ה-IP של הגשר באפליקציית Hue</li>
                        <li>גש ל-http://[IP]/debug/clip.html</li>
                        <li>לחץ על כפתור הגשר ושלח POST ל-/api</li>
                        <li>העתק את ה-username שקיבלת</li>
                      </ol>
                    </div>
                    <div className="space-y-2">
                      <Label>כתובת IP של הגשר</Label>
                      <Input
                        value={settings.philipsHue.bridgeIp}
                        onChange={(e) => {
                          const newSettings = saveSmartHomeSettings({
                            philipsHue: { ...settings.philipsHue, bridgeIp: e.target.value }
                          });
                          setSettings(newSettings);
                        }}
                        placeholder="192.168.1.xxx"
                        dir="ltr"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>שם משתמש (Username)</Label>
                      <Input
                        value={settings.philipsHue.username}
                        onChange={(e) => {
                          const newSettings = saveSmartHomeSettings({
                            philipsHue: { ...settings.philipsHue, username: e.target.value }
                          });
                          setSettings(newSettings);
                        }}
                        placeholder="הזן את ה-username"
                        dir="ltr"
                      />
                    </div>
                    <Button variant="outline" onClick={loadHueLights} className="gap-2">
                      <Check className="w-4 h-4" />
                      בדוק חיבור
                    </Button>
                  </div>
                )}
              </div>

              {/* Home Assistant Settings */}
              <div className={cn(
                "rounded-xl border p-4 space-y-4",
                settings.homeAssistant.enabled ? "border-green-500 bg-green-500/5" : "border-border"
              )}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Server className={cn("w-6 h-6", settings.homeAssistant.enabled ? "text-green-500" : "text-muted-foreground")} />
                    <h3 className="font-bold">Home Assistant</h3>
                  </div>
                  <Switch
                    checked={settings.homeAssistant.enabled}
                    onCheckedChange={(checked) => {
                      const newSettings = saveSmartHomeSettings({
                        homeAssistant: { ...settings.homeAssistant, enabled: checked }
                      });
                      setSettings(newSettings);
                    }}
                  />
                </div>
                
                {settings.homeAssistant.enabled && (
                  <div className="space-y-3 pt-2 border-t border-border/50">
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm">
                      <p className="font-medium mb-2">הוראות התחברות:</p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>פתח את Home Assistant בדפדפן</li>
                        <li>לך להגדרות → משתמשים → פרופיל</li>
                        <li>גלול ל-"Long-Lived Access Tokens"</li>
                        <li>צור אסימון חדש והעתק אותו</li>
                      </ol>
                    </div>
                    <div className="space-y-2">
                      <Label>כתובת השרת</Label>
                      <Input
                        value={settings.homeAssistant.serverUrl}
                        onChange={(e) => {
                          const newSettings = saveSmartHomeSettings({
                            homeAssistant: { ...settings.homeAssistant, serverUrl: e.target.value }
                          });
                          setSettings(newSettings);
                        }}
                        placeholder="http://homeassistant.local:8123"
                        dir="ltr"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>אסימון גישה (Access Token)</Label>
                      <Input
                        value={settings.homeAssistant.accessToken}
                        onChange={(e) => {
                          const newSettings = saveSmartHomeSettings({
                            homeAssistant: { ...settings.homeAssistant, accessToken: e.target.value }
                          });
                          setSettings(newSettings);
                        }}
                        placeholder="הזן את האסימון"
                        type="password"
                        dir="ltr"
                      />
                    </div>
                    <Button variant="outline" onClick={loadHAEntities} className="gap-2">
                      <Check className="w-4 h-4" />
                      בדוק חיבור
                    </Button>
                  </div>
                )}
              </div>

              {/* Webhook Info */}
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Webhook className="w-6 h-6 text-primary" />
                  <h3 className="font-bold">כפתורי Webhook</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  הוסף כפתורים מלשונית "כפתורים". ניתן לחבר ל-Zapier, Make, IFTTT או n8n.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

      </div>
    </MainLayout>
  );
}
